import React, { useEffect, useMemo, useRef } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import classnames from "classnames";
import { useGlobal } from "reactn";
import { useResponsive } from "@outplayed/responsive";
import { MOBILE_SLIDE_GLOBAL_KEY } from "lib/constants";
import { decimalToPercentage, getWoWTierColor } from "../../../lib/tierHelper";
import SpecLink from "./SpecLink";
import Talents from "./Talents";
import { getTierArrowMovedIcon } from "./TierListHelper";
import { numberWithCommas } from "lib/format_helper";
import { TooltipContainer } from "@outplayed/tooltips";
import { ReactComponent as TooltipIcon } from "svg/question-line.svg";
import { getFormattedRole, formatPercent, getFormattedEncounterType } from "../../../lib/wowHelpers";

const Tooltip = (props) => {
  const { text } = props;
  return (
    <TooltipContainer tooltipInfo={<div>{text}</div>}>
      <div className="tooltip-container">
        <TooltipIcon className="tooltip-icon" />
      </div>
    </TooltipContainer>
  );
};

const PvpTierListsTable = (props) => {
  const { data, talents, role, encounterType, stat } = props;
  const prevSort = useRef(null);
  const { currentBreakpoint } = useResponsive();
  const [mobileSlideClass] = useGlobal(MOBILE_SLIDE_GLOBAL_KEY);
  const isMobileSmall = currentBreakpoint === "MOBILE_SMALL" || currentBreakpoint === "MOBILE_MEDIUM";

  const columns = useMemo(
    () =>
      data.length > 0
        ? [
            {
              Header: (
                <div className="header-text">
                  Rank
                  <Tooltip text="Rank based on selected metric. Green and Red arrows show movement of the spec's tier ranking compared to all the other specs." />
                </div>
              ),
              accessor: "arrow",
              disableSortBy: true,
              minWidth: 80,
              maxWidth: 80,
              Cell: (props) => (
                <div div style={{ position: "relative" }}>
                  <div
                    style={
                      getTierArrowMovedIcon(props.sortedRows[props.index].original.arrow_indicator) !== null // Enter the value here
                        ? { position: "absolute", left: "50%", transform: "translateX(-50%)" }
                        : { position: "absolute", left: "50%", transform: "translateX(-50%) translateY(-50%)", top: "50%" }
                    }
                  >
                    {props.index + 1}
                  </div>
                  <div style={{ marginLeft: "40px", marginTop: "-2px" }}>
                    {
                      getTierArrowMovedIcon(props.sortedRows[props.index].original.arrow_indicator) // Enter the value here
                    }
                  </div>
                </div>
              ),
            },
            {
              Header: (
                <div className="header-text">
                  Tier
                  <Tooltip text="We calculate spec tier using a combination of a spec's average rating and how often they show up on the selected pvp leaderboard." />
                </div>
              ),
              accessor: (d) => d,
              id: "tier",
              minWidth: 40,
              maxWidth: 90,
              sortType: (a, b) => {
                const rankingA = Number(a.original.modified_rating);
                const rankingB = Number(b.original.modified_rating);
                return rankingB - rankingA;
              },
              Cell: ({ cell: { value } }) => (
                <span className={getWoWTierColor(value.tier)}>
                  <b>{value.tier}</b>
                </span>
              ),
            },
            {
              Header: isMobileSmall || currentBreakpoint === "MOBILE_LARGE" ? "Spec." : "Specialization",
              accessor: (d) => d,
              className: "spec-column",
              minWidth: 40,
              maxWidth: 260,
              sortType: (a, b) => {
                // TWWTODO: Add hero name to sorting
                const nameA = a.original.spec + a.original.class;
                const nameB = b.original.spec + b.original.class;
                return nameA.localeCompare(nameB);
              },
              Cell: ({ cell: { value } }) => <SpecLink data={value} encounterType={encounterType} />,
            },
            {
              Header: (
                <div className="header-text">
                  {isMobileSmall || currentBreakpoint === "MOBILE_LARGE" ? "Avg. Rating" : "Average Rating"}
                  <Tooltip text="The Average rating of all players of each spec on the selected pvp leaderboard." />
                </div>
              ),
              accessor: "average_rating",
              minWidth: 70,
              maxWidth: 100,
              sortType: (a, b) => {
                return b.original.average_rating - a.original.average_rating;
              },
              Cell: ({ cell: { value } }) => <span>{numberWithCommas(value)}</span>,
            },
            {
              Header: (
                <div className="header-text">
                  Popularity
                  <Tooltip text="Popularity is how often this spec appears on the selected pvp leaderboard." />
                </div>
              ),
              accessor: "popularity",
              minWidth: 70,
              maxWidth: 100,
              sortType: (a, b) => {
                return b.original.popularity - a.original.popularity;
              },
              Cell: ({ cell: { value } }) => <span>{decimalToPercentage(value / 100)}</span>,
            },
            {
              Header: (
                <div className="header-text">
                  Build
                  <Tooltip text="This section highlights the final talents selected from the spec tree to give a preview of what Talent build is the most successful in competitive PvP." />
                </div>
              ),
              accessor: (d) => d,
              id: "build",
              disableSortBy: true,
              Cell: ({ cell: { value } }) => <Talents data={value} talents={talents} encounterType={encounterType} />,
              minWidth: 60,
              maxWidth: 300,
            },
            {
              Header: (
                <div className="header-text">
                  Players
                  <Tooltip text="The number of players." />
                </div>
              ),
              accessor: "count",
              id: "count",
              Cell: ({ cell: { value } }) => <span>{numberWithCommas(value)}</span>,
              minWidth: 95,
              maxWidth: 100,
              sortType: (a, b) => {
                return b.original.count - a.original.count;
              },
            },
          ]
        : [],
    [currentBreakpoint, data, isMobileSmall, stat],
  );

  const hiddenColumns = [isMobileSmall && "build", isMobileSmall && "total_pop", role === "dps" && "hps"];

  const columnStyle = (column) => ({
    minWidth: column.totalMinWidth,
    maxWidth: column.totalMaxWidth,
    flex: `1 1 100%`,
  });

  const { getTableBodyProps, getTableProps, headerGroups, page, prepareRow, setPageSize, state } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: 100,
        pageIndex: 0,
        sortBy: prevSort.current || [{ id: "tier", desc: false }],
        hiddenColumns,
      },
      disableSortRemove: true,
    },
    useSortBy,
    usePagination,
  );

  useEffect(() => {
    setPageSize(100);
  }, [currentBreakpoint]);

  useEffect(() => {
    if (prevSort.current) {
      if (prevSort.current[0].id !== state.sortBy[0].id || prevSort.current[0].desc !== state.sortBy[0].desc) {
        prevSort.current = state.sortBy;
        setPageSize(100);
      }
    } else {
      prevSort.current = state.sortBy;
    }
  }, [state.sortBy]);

  return (
    <div>
      <div
        {...getTableProps({
          className: "content-section ReactTable ugg-table-2 tier-list",
        })}
      >
        <div className="tier-list-text-container">
          <div className="content-section-header">
            {getFormattedRole(role)} Rankings for {getFormattedEncounterType(encounterType)}
          </div>
          <div className="description">
            We calculate each spec's {getFormattedRole(role)} rankings in PvP by evaluating ratings from the very best players and
            factoring in the frequency of each spec's appearances on each of the pvp ladders. We believe this gives the most
            accurate rankings of every {getFormattedRole(role)} spec's performance at the highest levels of endgame PvP gameplay.
          </div>
        </div>
        <div className={`rt-thead -header ${mobileSlideClass}`}>
          {headerGroups.map((headerGroup, index) => (
            <div key={index} {...headerGroup.getHeaderGroupProps({ className: "rt-tr" })}>
              {headerGroup.headers.map((column, index) => {
                const thClassNames = classnames([
                  "rt-th",
                  column.headerClassName,
                  column.isSorted && !column.isSortedDesc ? "-sort-asc" : "",
                  column.isSorted && column.isSortedDesc ? "-sort-desc" : "",
                ]);

                return (
                  <div
                    key={index}
                    {...column.getHeaderProps(
                      column.getSortByToggleProps({
                        className: thClassNames,
                        style: columnStyle(column),
                      }),
                    )}
                  >
                    {column.render("Header")}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps({ className: "rt-tbody" })}>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              <div key={i} className="rt-tr-group">
                <div {...row.getRowProps({ className: "rt-tr" })}>
                  {row.cells.map((cell, index) => {
                    const tdClassNames = classnames("rt-td", cell.column.className, {
                      sorted: cell.column.isSorted,
                      "is-in-odd-row": i % 2 === 0,
                    });

                    return (
                      <div
                        key={index}
                        {...cell.getCellProps({
                          className: tdClassNames,
                          style: columnStyle(cell.column),
                        })}
                      >
                        {cell.render("Cell", { index: i })}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PvpTierListsTable;
